import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiBookOpen,
  FiHelpCircle,
  FiSend,
  FiX,
  FiZap,
} from 'react-icons/fi';
import { renderMarkdownToSafeHtml } from '../../../../utils/safeHtml';
import './IELTSCourseCopilotPanel.css';

export default function IELTSCourseCopilotPanel({
  activeContext = '',
  courseTitle = '',
  generating = false,
  messages = [],
  onClose,
  onSend,
  showCloseButton = true,
}) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, generating]);

  const contextLabel = activeContext || courseTitle || 'this lesson';

  const quickActions = useMemo(
    () => [
      {
        label: 'Explain this',
        icon: <FiBookOpen size={13} />,
        prompt: `Explain "${contextLabel}" in simple IELTS-focused terms with examples.`,
      },
      {
        label: 'Give examples',
        icon: <FiHelpCircle size={13} />,
        prompt: `Give me more IELTS-style examples for "${contextLabel}".`,
      },
      {
        label: 'Quiz me',
        icon: <FiZap size={13} />,
        prompt: `Quiz me on "${contextLabel}" with 3 short practice questions.`,
      },
    ],
    [contextLabel]
  );

  const sendInput = () => {
    const trimmed = input.trim();
    if (!trimmed || generating) {
      return;
    }

    onSend(trimmed);
    setInput('');
  };

  return (
    <aside className="ielts-copilot">
      <div className="ielts-copilot__header">
        <div className="ielts-copilot__brand">
          <div className="ielts-copilot__brand-mark" aria-hidden>
            <img
              className="ielts-copilot__brand-logo"
              src="/liwaza-short.png"
              alt=""
            />
          </div>
          <div>
            <p className="ielts-copilot__eyebrow">Liwaza AI</p>
            <h3 className="ielts-copilot__title">Lesson help</h3>
          </div>
        </div>
        {showCloseButton && (
          <button
            type="button"
            className="ielts-copilot__close"
            aria-label="Close lesson help"
            onClick={onClose}
          >
            <FiX size={16} />
          </button>
        )}
      </div>

      <div className="ielts-copilot__context">
        <span>Discussing</span>
        <strong>{contextLabel}</strong>
      </div>

      <div className="ielts-copilot__messages">
        {messages.map((message, idx) => (
          <div
            key={`${message.role}-${idx}`}
            className={`ielts-copilot__message ielts-copilot__message--${message.role}`}
          >
            <div
              className="ielts-copilot__bubble"
              dangerouslySetInnerHTML={{
                __html: renderMarkdownToSafeHtml(message.content),
              }}
            />
          </div>
        ))}

        {generating && (
          <div className="ielts-copilot__message ielts-copilot__message--ai">
            <div className="ielts-copilot__typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="ielts-copilot__quick-actions">
        {quickActions.map((action) => (
          <button
            key={action.label}
            type="button"
            className="ielts-copilot__quick-action"
            onClick={() => onSend(action.prompt)}
          >
            {action.icon}
            <span>{action.label}</span>
          </button>
        ))}
      </div>

      <div className="ielts-copilot__composer">
        <div className="ielts-copilot__composer-shell">
          <textarea
            className="ielts-copilot__input"
            placeholder="Ask about this lesson..."
            rows={4}
            value={input}
            onChange={(event) => setInput(event.target.value)}
          />
          <button
            type="button"
            className="ielts-copilot__send"
            disabled={!input.trim() || generating}
            onClick={sendInput}
            aria-label="Send"
            title="Send"
          >
            <FiSend size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
}
